/**
 * supabaseAuth.js
 * Saved email connections (Microsoft and Google sign-ins).
 *
 * The browser NEVER reads or writes public.auth_tokens any more (plain-text access and refresh tokens were readable and writable by every
 * signed-in user). Everything goes through the server:
 *   /api/email-connections  list (status only, no tokens), connect (save a sign-in once), disconnect
 *   /api/ms-access-token    a short-lived ACCESS token for a saved connection, refreshed on the server; the refresh token never reaches the browser
 *
 * Microsoft sign-ins last about a day (SPA refresh-token cap). Sending does not depend on them: Charlie sends with the app-level token.
 */

import { authFetch } from './authFetch';

// The reason the last save failed (for the screen to show). Saves still return true/false so callers stay as they were.
export let lastStoreTokenError = null;

async function post(path, payload) {
  const res = await authFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  const out = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, out };
}

// ─── SHARED ────────────────────────────────────────────────────────────

/**
 * Save a sign-in for a partner + provider. The server checks you may use that partner and, for Microsoft, that Microsoft accepts the token.
 * Returns true when saved.
 */
export async function storeTokenInSupabase({ partnerId, provider, accessToken, refreshToken, expiresAt, expiresIn, idToken, email, scope }) {
  const seconds = expiresIn || (expiresAt ? Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000) : 3600);
  const { ok, out } = await post('/api/email-connections', {
    action: 'connect', partner_id: partnerId, provider, access_token: accessToken, refresh_token: refreshToken || null,
    expires_in: seconds, id_token: idToken || null, email: email || null, scope: scope || null,
  });
  lastStoreTokenError = ok ? null : (out.message || out.error || 'The connection could not be saved');
  if (!ok) console.error('[supabaseAuth] connect error:', lastStoreTokenError);
  return ok;
}

/**
 * The saved connection's STATUS for a partner + provider (who, when it expires, whether it has expired). No token.
 * Returns null if not found.
 */
export async function getConnectionStatus(partnerId, provider) {
  const all = await loadAllTokens();
  const hit = all.find((c) => c.partner_id === partnerId && c.provider === provider);
  return hit || null;
}

/**
 * Remove a saved connection (on disconnect).
 */
export async function deleteTokenFromSupabase(partnerId, provider) {
  const { ok, out } = await post('/api/email-connections', { action: 'disconnect', partner_id: partnerId, provider });
  if (!ok) console.error('[supabaseAuth] disconnect error:', out.message || out.error);
  return ok;
}

/**
 * Status of every saved connection (partner, provider, email, expires_at, scope, expired). Never tokens.
 */
export async function loadAllTokens() {
  const { ok, out } = await post('/api/email-connections', { action: 'list' });
  if (!ok) {
    console.error('[supabaseAuth] loadAllTokens error:', out.message || out.error);
    return [];
  }
  return out.connections || [];
}

/**
 * A live access token for a partner + provider, or null (not connected, or needs signing in again).
 * The server refreshes an expired Microsoft token for us; Google has none to refresh.
 */
export async function fetchAccessToken(partnerId, provider) {
  if (!partnerId) return null;
  try {
    const { ok, out } = await post('/api/ms-access-token', { partner_id: partnerId, provider });
    return ok ? out.access_token || null : null;
  } catch (e) {
    console.warn('[supabaseAuth] access token request failed:', e.message);
    return null;
  }
}

// ─── MICROSOFT ───────────────────────────────────────────────────────────

/**
 * After a successful PKCE popup, save the sign-in. The browser exchanged the code once; this posts it and never reads it back.
 * Accepts: { accessToken, refreshToken, expiresIn, idToken }
 */
export async function storeMsTokenInSupabase(partnerId, tokenData) {
  if (!tokenData?.accessToken) return false;
  return storeTokenInSupabase({
    partnerId,
    provider:     'microsoft',
    accessToken:  tokenData.accessToken,
    refreshToken: tokenData.refreshToken || null,
    expiresIn:    tokenData.expiresIn || 3600,
    idToken:      tokenData.idToken || null,
  });
}

/**
 * Get a valid Microsoft access token for a partner (refreshed on the server if needed). Returns null if it needs signing in again.
 */
export async function getMsTokenForPartner(partnerId) {
  return fetchAccessToken(partnerId, 'microsoft');
}

// ─── GOOGLE ──────────────────────────────────────────────────

/**
 * After a successful Google GIS flow, save the sign-in. GIS tokens expire in ~3600s and have no refresh token.
 */
export async function storeGoogleTokenInSupabase(partnerId, email, gisResponse) {
  if (!gisResponse?.access_token) return false;
  return storeTokenInSupabase({
    partnerId,
    provider:     'google',
    accessToken:  gisResponse.access_token,
    refreshToken: null,
    expiresIn:    gisResponse.expires_in || 3600,
    email,
    scope:        gisResponse.scope || null,
  });
}

/**
 * A live Google access token for a partner, or null (null = needs signing in again via the GIS popup).
 */
export async function getGoogleTokenForPartner(partnerId) {
  return fetchAccessToken(partnerId, 'google');
}
